import { useEffect, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from './supabase.client';

/**
 * Secondary auth gate that obtains a Supabase session for master-data
 * Edge Function calls. The outer Verse8 admin gate (admin-app's AuthGate +
 * AdminGate) already verifies the user is an admin; this gate only exists
 * to populate a Supabase JWT.
 */
export function SupabaseLoginGate({ children }: { children: ReactNode }) {
  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setEmail(data.session?.user.email ?? null);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setEmail(session?.user.email ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading Supabase session…</div>;
  }
  if (!email) return <LoginForm />;

  return <>{children}</>;
}

function LoginForm() {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <Card className="mx-auto mt-16 max-w-sm">
      <CardHeader>
        <CardTitle>Master Data — Supabase sign-in</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          className="flex flex-col gap-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setErr(null);
            setBusy(true);
            const { error } = await supabase.auth.signInWithPassword({ email, password: pw });
            setBusy(false);
            if (error) setErr(error.message);
          }}
        >
          <div className="flex flex-col gap-1">
            <Label htmlFor="md-email">Email</Label>
            <Input
              id="md-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email"
              required
              autoComplete="username"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="md-pw">Password</Label>
            <Input
              id="md-pw"
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="password"
              required
              autoComplete="current-password"
            />
          </div>
          <Button type="submit" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
          {err && <p className="text-sm text-destructive">{err}</p>}
        </form>
      </CardContent>
    </Card>
  );
}
